import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CurrencyDisplay } from '../components/DirhamSymbol';
import { API, getAuthHeader } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import UploadMotorcycleDocumentDialog from '../components/UploadMotorcycleDocumentDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Wrench, AlertTriangle, User, Calendar, DollarSign, FileText, FileDown, ArrowRightLeft, RotateCcw, Car, MoreVertical, Eye, ArrowRight, Clock, Plus } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { generateHandoverPDF, generateTakeoverPDF, generateAccidentPDF } from '../utils/handoverTakeoverPdfUtils';
import { formatDateDisplay, formatDateTimeDisplay } from '../utils/dateUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';

const MotorcycleHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useDeviceDetection();
  const [history, setHistory] = useState(null);
  const [transactionHistory, setTransactionHistory] = useState(null);
  const [motorcycleDocuments, setMotorcycleDocuments] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
    fetchTransactionHistory();
    fetchMotorcycleDocuments();
  }, [id]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/motorcycles/${id}/history`, {
        headers: getAuthHeader()
      });
      setHistory(response.data);
    } catch (error) {
      toast.error('Failed to fetch motorcycle history');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionHistory = async () => {
    try {
      const response = await axios.get(`${API}/motorcycles/${id}/transaction-history`, {
        headers: getAuthHeader()
      });
      setTransactionHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch transaction history:', error);
    }
  };

  const fetchMotorcycleDocuments = async () => {
    try {
      const response = await axios.get(`${API}/motorcycles/${id}/documents`, {
        headers: getAuthHeader()
      });
      setMotorcycleDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch motorcycle documents:', error);
      setMotorcycleDocuments({ documents: [], motorcycle_id: id });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  if (!history) {
    return <div className="flex items-center justify-center h-96">Motorcycle not found</div>;
  }

  const motorcycle = history.motorcycle;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/motorcycles')}
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Motorcycles
          </Button>
          <h1 className="text-4xl font-bold text-slate-800">Motorcycle History</h1>
          <p className="text-slate-600 mt-1">{motorcycle.plate_number} - Complete history and timeline</p>
        </div>
      </div>

      {/* Motorcycle Info Card */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle>Motorcycle Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-600">Plate Number</p>
              <p className="font-semibold">{motorcycle.plate_number}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Model</p>
              <p className="font-semibold">{motorcycle.model_name} {motorcycle.model_version}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Chassis Number</p>
              <p className="font-semibold">{motorcycle.chassis_number}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Status</p>
              <Badge className={
                motorcycle.status === 'available' ? 'bg-green-600' :
                motorcycle.status === 'in_use' || motorcycle.status === 'in-use' ? 'bg-blue-600' :
                motorcycle.status === 'maintenance' ? 'bg-orange-600' : 'bg-gray-600'
              }>
                {motorcycle.status}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-slate-600">Lease Status</p>
              <Badge className={motorcycle.lease_status === 'leased' ? 'bg-purple-600' : 'bg-gray-600'}>
                {motorcycle.lease_status}
              </Badge>
            </div>
            {/* Currently Assigned Rider */}
            {motorcycle.assigned_rider_name && (
              <div>
                <p className="text-sm text-slate-600">Currently Assigned Rider</p>
                <p className="font-semibold text-blue-700">{motorcycle.assigned_rider_name}</p>
              </div>
            )}
            {motorcycle.lessee_name && (
              <div>
                <p className="text-sm text-slate-600">Current Lessee</p>
                <p className="font-semibold">{motorcycle.lessee_name}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-slate-600">Insurance Provider</p>
              <p className="font-semibold">{motorcycle.insurance_provider || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Insurance Expiry</p>
              <p className="font-semibold">{formatDateDisplay(motorcycle.insurance_expiry_date) || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Maintenance</CardTitle>
            <Wrench className="w-4 h-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{history.maintenance_history.length}</div>
            <p className="text-xs text-slate-600">Records</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Maintenance Cost</CardTitle>
            <DollarSign className="w-4 h-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold"><CurrencyDisplay amount={history.total_maintenance_cost || 0} size={20} /></div>
            <p className="text-xs text-slate-600">Total spent</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Fines</CardTitle>
            <AlertTriangle className="w-4 h-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{history.fines_history?.length || 0}</div>
            <p className="text-xs text-slate-600">Violations</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fines Amount</CardTitle>
            <DollarSign className="w-4 h-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold"><CurrencyDisplay amount={history.total_fines_amount || 0} size={20} /></div>
            <p className="text-xs text-slate-600">Total fines</p>
          </CardContent>
        </Card>
      </div>

      {/* History Tabs */}
      <Card className="border-0 shadow-lg">
        <CardContent className="pt-6">
          <Tabs defaultValue="transactions" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="transactions">Transaction History</TabsTrigger>
              <TabsTrigger value="maintenance">Maintenance History</TabsTrigger>
              <TabsTrigger value="fines">Fines History</TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-motorcycle-documents">Motorcycle Documents</TabsTrigger>
              <TabsTrigger value="riders">Rider Assignments</TabsTrigger>
            </TabsList>

            {/* Transaction History Tab */}
            <TabsContent value="transactions" className="space-y-4">
              {/* Summary Stats */}
              {transactionHistory && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <Card className="border bg-blue-50">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Handovers</p>
                        <p className="text-xl font-bold text-blue-700">{transactionHistory.summary.total_handovers}</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border bg-yellow-50">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="p-2 bg-yellow-100 rounded-lg">
                        <RotateCcw className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Takeovers</p>
                        <p className="text-xl font-bold text-yellow-700">{transactionHistory.summary.total_takeovers}</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border bg-red-50">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Accidents</p>
                        <p className="text-xl font-bold text-red-700">{transactionHistory.summary.total_accidents}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* All Transactions Table */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Lessee</TableHead>
                      <TableHead>Rider</TableHead>
                      <TableHead>Technician/Details</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!transactionHistory || transactionHistory.all_transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                          No transaction records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactionHistory.all_transactions.map((transaction, idx) => (
                        <TableRow key={`${transaction.transaction_type}-${transaction.id || idx}`}>
                          <TableCell>
                            <div className="font-medium">{formatDateDisplay(transaction.transaction_date)}</div>
                            <div className="text-xs text-slate-500">{transaction.transaction_time}</div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              transaction.transaction_type === 'handover' ? 'bg-blue-100 text-blue-800' :
                              transaction.transaction_type === 'takeover' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }>
                              {transaction.transaction_type === 'handover' ? 'Handover' :
                               transaction.transaction_type === 'takeover' ? `Takeover (${transaction.takeover_type || 'N/A'})` :
                               'Accident'}
                            </Badge>
                          </TableCell>
                          <TableCell>{transaction.lessee_name || 'N/A'}</TableCell>
                          <TableCell>{transaction.rider_name || 'N/A'}</TableCell>
                          <TableCell>
                            {transaction.transaction_type === 'accident' ? (
                              <div>
                                <Badge className={
                                  transaction.police_report_type === 'green' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }>
                                  {transaction.police_report_type?.toUpperCase()} Report
                                </Badge>
                                {transaction.estimated_damage_cost && (
                                  <div className="text-xs text-slate-500 mt-1">
                                    Est. Cost: <CurrencyDisplay amount={transaction.estimated_damage_cost} />
                                  </div>
                                )}
                              </div>
                            ) : (
                              transaction.technician_name || 'N/A'
                            )}
                          </TableCell>
                          <TableCell>{transaction.location || transaction.location_name || 'N/A'}</TableCell>
                          <TableCell className="max-w-xs">
                            <div className="truncate text-sm text-slate-600">
                              {transaction.notes || transaction.description || transaction.temporary_reason || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                try {
                                  const motorcycle = transactionHistory.motorcycle;
                                  if (transaction.transaction_type === 'handover') {
                                    await generateHandoverPDF(transaction, motorcycle);
                                  } else if (transaction.transaction_type === 'takeover') {
                                    await generateTakeoverPDF(transaction, motorcycle);
                                  } else if (transaction.transaction_type === 'accident') {
                                    await generateAccidentPDF(transaction, motorcycle);
                                  }
                                  toast.success('PDF downloaded successfully');
                                } catch (error) {
                                  toast.error('Failed to generate PDF');
                                  console.error('PDF generation error:', error);
                                }
                              }}
                              title="Download PDF"
                            >
                              <FileDown className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Maintenance History */}
            <TabsContent value="maintenance" className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Technician</TableHead>
                      <TableHead>Rider</TableHead>
                      <TableHead>Repair Items</TableHead>
                      <TableHead>Cost (Dh)</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.maintenance_history.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                          No maintenance records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      history.maintenance_history.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell>
                            <div>{formatDateDisplay(record.maintenance_date)}</div>
                            <div className="text-xs text-slate-500">{record.maintenance_time}</div>
                          </TableCell>
                          <TableCell>
                            <Badge>{record.type}</Badge>
                          </TableCell>
                          <TableCell>{record.technician}</TableCell>
                          <TableCell>{record.rider_name || 'N/A'}</TableCell>
                          <TableCell className="text-sm">
                            {record.repair_items?.map(item => item.item_name).join(', ') || 'N/A'}
                          </TableCell>
                          <TableCell className="font-semibold"><CurrencyDisplay amount={record.total_cost || 0} /></TableCell>
                          <TableCell>
                            <Badge className={
                              record.status === 'completed' ? 'bg-green-600' :
                              record.status === 'in-progress' ? 'bg-blue-600' : 'bg-orange-600'
                            }>
                              {record.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Fines History */}
            <TabsContent value="fines" className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Rider</TableHead>
                      <TableHead>Lessee</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Fine Amount</TableHead>
                      <TableHead>Admin Fees</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.fines_history.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                          No fines found
                        </TableCell>
                      </TableRow>
                    ) : (
                      history.fines_history.map((fine) => {
                        const total = fine.amount + (fine.admin_fees || 0);
                        return (
                          <TableRow key={fine.id}>
                            <TableCell>
                              <div>{formatDateDisplay(fine.fine_date)}</div>
                              <div className="text-xs text-slate-500">{fine.fine_time}</div>
                            </TableCell>
                            <TableCell>{fine.rider_name}</TableCell>
                            <TableCell>{fine.lessee_name || 'N/A'}</TableCell>
                            <TableCell className="max-w-xs truncate">{fine.reason}</TableCell>
                            <TableCell><CurrencyDisplay amount={fine.amount || 0} /></TableCell>
                            <TableCell><CurrencyDisplay amount={fine.admin_fees || 0} /></TableCell>
                            <TableCell className="font-semibold"><CurrencyDisplay amount={total || 0} /></TableCell>
                            <TableCell>
                              <Badge className={fine.status === 'paid' ? 'bg-green-600' : 'bg-orange-600'}>
                                {fine.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Rider Assignments Timeline */}
            <TabsContent value="documents" className="space-y-4">
              <div className="flex justify-end">
                <Button
                  size="sm"
                  className="gap-2"
                  onClick={() => setUploadDialogOpen(true)}
                  data-testid="open-upload-motorcycle-doc-btn"
                >
                  <Plus className="w-4 h-4" /> Upload Document
                </Button>
              </div>
              {!motorcycleDocuments || motorcycleDocuments.documents.length === 0 ? (
                <div className="text-center text-slate-500 py-12" data-testid="documents-empty">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-lg font-medium">No documents uploaded yet</p>
                  <p className="text-sm mt-1">Registration Card, Food Permit, Signed Handover / Takeover forms and future documents will appear here</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Document</TableHead>
                        <TableHead>Rider / Context</TableHead>
                        <TableHead>Storage</TableHead>
                        <TableHead>Uploaded At</TableHead>
                        <TableHead>Uploaded By</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {motorcycleDocuments.documents.map((doc, i) => (
                        <TableRow key={i} data-testid={`doc-row-${doc.doc_type}`}>
                          <TableCell className="font-medium">{doc.label}</TableCell>
                          <TableCell className="text-slate-600 text-xs">
                            {doc.rider_name ? (<div><strong>{doc.rider_name}</strong></div>) : null}
                            {doc.form_date ? (<div>{doc.form_date}{doc.form_time ? ` · ${doc.form_time}` : ''}</div>) : null}
                            {!doc.rider_name && !doc.form_date ? (doc.filename || '—') : null}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              doc.storage === 'SharePoint' ? 'border-blue-300 text-blue-700' :
                              doc.storage === 'GridFS' ? 'border-purple-300 text-purple-700' :
                              'border-slate-300 text-slate-700'
                            }>
                              {doc.storage || 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {doc.uploaded_at ? formatDateTimeDisplay(doc.uploaded_at) : '—'}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{doc.uploaded_by || '—'}</TableCell>
                          <TableCell className="text-right space-x-1">
                            {doc.url && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => window.open(doc.url, '_blank')} data-testid={`view-doc-${doc.doc_type}`}>
                                  <Eye className="w-4 h-4 mr-1" /> View
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => {
                                  const a = document.createElement('a');
                                  a.href = doc.url; a.download = doc.filename || `${doc.doc_type}.pdf`;
                                  a.target = '_blank'; a.rel = 'noopener noreferrer';
                                  document.body.appendChild(a); a.click(); a.remove();
                                }} data-testid={`download-doc-${doc.doc_type}`}>
                                  <FileDown className="w-4 h-4 mr-1" /> Download
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <UploadMotorcycleDocumentDialog
                open={uploadDialogOpen}
                onOpenChange={setUploadDialogOpen}
                motorcycleId={id}
                onSaved={fetchMotorcycleDocuments}
              />
            </TabsContent>

            {/* Rider Assignments Timeline */}
            <TabsContent value="riders" className="space-y-4">
              {history.rider_assignments.length === 0 ? (
                <div className="text-center text-slate-500 py-12">
                  <p className="text-lg font-medium">No rider assignments found</p>
                  <p className="text-sm mt-1">Handover and takeover records will appear here</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Vertical timeline line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" style={{ zIndex: 0 }} />

                  <div className="space-y-0">
                    {history.rider_assignments.map((assignment, idx) => {
                      const isCurrent = assignment.status === 'Currently Assigned';
                      const isAccident = assignment.status?.includes('Accident');
                      const isNoHandover = assignment.status?.includes('no handover');

                      return (
                        <div key={idx} className="relative pl-14" data-testid={`rider-assignment-${idx}`}>
                          {/* Timeline node */}
                          <div className={`absolute left-4 w-5 h-5 rounded-full border-2 z-10 ${
                            isCurrent ? 'bg-green-500 border-green-600 ring-4 ring-green-100' :
                            isAccident ? 'bg-red-500 border-red-600 ring-4 ring-red-100' :
                            'bg-slate-400 border-slate-500'
                          }`} style={{ top: '24px' }} />

                          {/* Assignment Card */}
                          <div className={`mb-4 rounded-lg border overflow-hidden ${
                            isCurrent ? 'border-green-300 shadow-md' :
                            isAccident ? 'border-red-300 shadow-md' :
                            'border-slate-200'
                          }`}>
                            {/* Card Header - Rider + Status */}
                            <div className={`px-4 py-3 flex items-center justify-between ${
                              isCurrent ? 'bg-green-50' :
                              isAccident ? 'bg-red-50' :
                              'bg-slate-50'
                            }`}>
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                  isCurrent ? 'bg-green-600' :
                                  isAccident ? 'bg-red-600' :
                                  'bg-slate-500'
                                }`}>
                                  {(assignment.rider_name || '?').charAt(0).toUpperCase()}
                                </div>
                                <span className="font-semibold text-slate-800">{assignment.rider_name || 'Unknown Rider'}</span>
                              </div>
                              <Badge className={
                                isCurrent ? 'bg-green-600' :
                                isAccident ? 'bg-red-600' :
                                isNoHandover ? 'bg-amber-600' :
                                'bg-slate-600'
                              } data-testid={`assignment-status-${idx}`}>
                                {assignment.status || 'Assigned'}
                              </Badge>
                            </div>

                            {/* Card Body - Visual Flow */}
                            <div className="px-4 py-4">
                              {/* Handover → Duration → Takeover flow */}
                              <div className="flex items-center gap-2 flex-wrap">
                                {/* Handover box */}
                                <div className={`flex-1 min-w-[140px] rounded-md p-3 ${
                                  assignment.start_date ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-dashed border-slate-300'
                                }`}>
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <ArrowRightLeft className="w-3.5 h-3.5 text-blue-600" />
                                    <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Handover</span>
                                  </div>
                                  <div className="text-sm font-medium text-slate-800">
                                    {assignment.start_date ? formatDateDisplay(assignment.start_date) : 'No record'}
                                  </div>
                                  {assignment.start_time && (
                                    <div className="text-xs text-slate-500">{assignment.start_time}</div>
                                  )}
                                </div>

                                {/* Arrow + Duration */}
                                <div className="flex flex-col items-center px-2">
                                  <ArrowRight className="w-5 h-5 text-slate-400" />
                                  {assignment.duration_days != null && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <Clock className="w-3 h-3 text-slate-400" />
                                      <span className="text-xs font-medium text-slate-500">{assignment.duration_days}d</span>
                                    </div>
                                  )}
                                </div>

                                {/* Takeover box */}
                                <div className={`flex-1 min-w-[140px] rounded-md p-3 ${
                                  assignment.end_date ? 'bg-amber-50 border border-amber-200' :
                                  isCurrent ? 'bg-green-50 border border-dashed border-green-300' :
                                  'bg-slate-50 border border-dashed border-slate-300'
                                }`}>
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <RotateCcw className="w-3.5 h-3.5 text-amber-600" />
                                    <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Takeover</span>
                                  </div>
                                  <div className="text-sm font-medium text-slate-800">
                                    {assignment.end_date ? formatDateDisplay(assignment.end_date) : (
                                      isCurrent ? <span className="text-green-600 italic">Active</span> : 'No record'
                                    )}
                                  </div>
                                  {assignment.end_time && (
                                    <div className="text-xs text-slate-500">{assignment.end_time}</div>
                                  )}
                                </div>
                              </div>

                              {/* Takeover Reason */}
                              {assignment.takeover_reason && (
                                <div className="mt-3 text-xs text-slate-600 bg-slate-50 rounded px-3 py-2">
                                  <span className="font-semibold text-slate-500">Reason:</span> {assignment.takeover_reason}
                                </div>
                              )}

                              {/* Processed By */}
                              {(assignment.handover_by || assignment.takeover_by) && (
                                <div className="flex gap-4 mt-2 text-xs text-slate-400">
                                  {assignment.handover_by && <span>Handover by: {assignment.handover_by}</span>}
                                  {assignment.takeover_by && <span>Takeover by: {assignment.takeover_by}</span>}
                                </div>
                              )}

                              {/* Notes */}
                              {(assignment.handover_notes || assignment.takeover_notes) && (
                                <div className="mt-2 text-xs text-slate-500 space-y-1">
                                  {assignment.handover_notes && <p><span className="font-medium">Notes:</span> {assignment.handover_notes}</p>}
                                  {assignment.takeover_notes && <p><span className="font-medium">Takeover notes:</span> {assignment.takeover_notes}</p>}
                                </div>
                              )}

                              {/* Accidents */}
                              {assignment.accidents && assignment.accidents.length > 0 && (
                                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                                  <span className="text-xs font-semibold text-red-700 flex items-center gap-1 mb-1">
                                    <AlertTriangle className="w-3 h-3" /> Accidents During Period
                                  </span>
                                  {assignment.accidents.map((acc, accIdx) => (
                                    <div key={accIdx} className="text-xs text-red-600 flex gap-2 ml-4">
                                      <span className="font-medium">{formatDateDisplay(acc.date)}</span>
                                      {acc.severity && <span>[{acc.severity}]</span>}
                                      <span>{acc.description}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default MotorcycleHistory;
